<template>
    <div class="col-sm-12 nopads mt-2">
        <div class="search-form px-3">
            <div class="col-sm-12 search-form-title">
                <font-awesome-icon icon="search" />
                {{ $t('common.search') }}
            </div>
            <div class="no-margin">
                <div class="row">
                    <div class="col-sm-6 px-2">
                        <span class="span-title">
                            {{ $t('ins_report.from_date') }}
                        </span>
                        <date-time-picker
                            v-model:date="fromDate"
                            class="routa-input"
                            format="dd.MM.yyyy HH:mm"
                            :enable-time-picker="true"
                            :placeholder="$t('areas.set_work_time')"
                        />
                    </div>

                    <div class="col-sm-6 px-2">
                        <span class="span-title">
                            {{ $t('ins_report.to_date') }}
                        </span>
                        <date-time-picker
                            v-model:date="toDate"
                            class="routa-input"
                            format="dd.MM.yyyy HH:mm"
                            :enable-time-picker="true"
                            :placeholder="$t('areas.set_work_time')"
                        />
                    </div>
                </div>

                <div class="row">
                    <div class="col-sm-6">
                        <RouteOptimizationContractSelector
                            ref="contractSelector"
                            v-model="contract"
                            :from="fromDate"
                            :to="toDate"
                            @contract-selected="setContract"
                        />
                    </div>

                    <div class="col-sm-6">
                        <RouteOptimizationOrderSelector
                            ref="orderSelector"
                            v-model="selectedOrder"
                            :contract="contract"
                        />
                    </div>
                </div>

                <div class="col-sm-12 button-container d-flex justify-content-end">
                    <button
                        type="submit"
                        class="btn btn-primary form-button d-flex align-items-center justify-content-center"
                        style="position: relative; min-width: 120px;"
                        @click.stop="onSubmit"
                    >
                        {{ $t('ins_report.search') }}
                        <div
                            v-if="loading"
                            class="spinner-border spinner-border-sm text-white"
                            role="status"
                            style="position: absolute; right: 10px;"
                        >
                            <span class="visually-hidden">Loading...</span>
                        </div>
                    </button>
                </div>
            </div>
        </div>

        <div
            v-if="!showResults"
            class="mt-4"
        >
            <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="mb-0">
                    {{ $t('route_optimization.search_results') }}
                </h5>
            </div>
            <div
                class="row"
                style="height: 250px;"
            >
                <div class="col-12">
                    <div
                        class="d-flex align-items-center justify-content-center h-100 border rounded text-muted"
                        style="height: 250px;"
                    >
                        {{ $t('common.no_results') || 'Use search to find trips' }}
                    </div>
                </div>
            </div>
        </div>

        <div
            v-if="showResults"
            class="mt-4"
        >
            <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="mb-0">
                    {{ $t('route_optimization.search_results') }} ({{ searchResults.length }})
                </h5>
                <button
                    v-if="searchResults.length > 0"
                    class="btn btn-outline-success btn-sm"
                    @click="toggleMap"
                >
                    {{ showMap ? $t('route_optimization.hide_map') : $t('route_optimization.show_map') }}
                </button>
            </div>
            <div
                v-if="searchResults.length === 0"
                class="text-muted"
            >
                {{ $t('common.no_results') || 'No trips found' }}
            </div>
            <div
                v-else
                class="row"
                style="height: 300px;"
            >
                <div :class="{'col-md-6': showMap, 'col-12': !showMap}">
                    <TripList
                        :key="searchResults.length"
                        :trips="searchResults"
                        :has-checkbox="true"
                        style="height: 300px; overflow-y: auto;"
                        @row-clicked="showTripOnMap"
                        @row-select="handleSelectedTrips"
                    />
                </div>
                <div
                    v-if="showMap"
                    class="col-md-6"
                >
                    <div
                        class="position-relative h-100"
                        style="border: 1px solid #BCBCBC; border-radius: 0.375rem; overflow: hidden;"
                    >
                        <map-container
                            ref="mapContainer"
                            :find-user="false"
                            :menu-enabled="false"
                            @on-map-changed="mapChanged"
                        />
                        <TripMap
                            v-if="map"
                            ref="tripMap"
                            :trip-results="searchResults"
                            :trip-item="selectedTrip"
                            :map="map"
                            :show-trip-info="false"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { restApi } from '../../mixins/RestApiMixin'
import { timeUtils } from '../../mixins/TimeUtils'
import RouteOptimizationContractSelector from "../RouteOptimizationContractSelector.vue";
import RouteOptimizationOrderSelector from "../RouteOptimizationOrderSelector.vue";
import DateTimePicker from "@/components/common/DateTimePicker";
import MapContainer from "@/components/map/MapContainer.vue";
import TripList from "./TripList.vue";
import TripMap from "./TripMap.vue";

export default {
    name: 'TripSearch',
    components: {
        RouteOptimizationContractSelector,
        RouteOptimizationOrderSelector,
        DateTimePicker,
        MapContainer,
        TripList,
        TripMap
    },
    mixins: [restApi, timeUtils],
    props: {
        user: {
            type: Object,
            default: null
        },
        equipment: {
            type: Object,
            default: null
        },
        preSelectedContract: {
            type: Object,
            default: null
        },
        isObserver: {
            type: Boolean,
            default: false
        },
        availableContracts: {
            type: Array,
            default: () => []
        },
        availableOrders: {
            type: Array,
            default: () => []
        },
        preSelectedOrders: {
            type: Array,
            default: () => []
        }
    },
    emits: ['selected-trips-changed', 'contract-changed', 'orders-changed'],
    data() {
        return {
            fromDate: '',
            toDate: '',
            contract: null,
            selectedOrder: null,
            loading: false,
            searchResults: [],
            showResults: false,
            showMap: false,
            selectedTripId: null,
            selectedTrips: [],
            map: null
        }
    },
    computed: {
        selectedTrip() {
            return this.searchResults.find((trip) => trip.id === this.selectedTripId)
        }
    },

    watch: {
        preSelectedContract(newVal) {
            if (newVal && (!this.contract || this.contract.id !== newVal.id)) {
                this.contract = newVal
            }
        },
        preSelectedOrders(newVal) {
            if (newVal && newVal.length > 0) {
                this.selectedOrder = newVal[0]
            }
        },
        selectedOrder(newVal) {
            this.$emit('orders-changed', newVal ? [newVal] : [])
        }
    },
    mounted() {
        if (!this.fromDate) {
            this.fromDate = this.preSelectedContract ? this.getDayStart() : this.getMonthStart()
        }
        if (this.preSelectedContract) {
            this.contract = this.preSelectedContract
        }
        if (this.preSelectedOrders && this.preSelectedOrders.length > 0) {
            this.selectedOrder = this.preSelectedOrders[0]
        }
    },
    methods: {
        setContract(contract) {
            this.contract = contract
            this.$emit('contract-changed', contract)
        },

        onSubmit() {
            const params = {}
            if (this.contract) {
                params.contract = this.contract.id
                params.contractor = this.contract.contractor.id
                params.customer = this.contract.customer.id
            }
            if (this.selectedOrder) {
                params.orders = [this.selectedOrder.id]
            }
            if (this.fromDate) {
                params.from = this.fromDate
            }
            if (this.toDate) {
                params.to = this.toDate
            }
            params.trip_notes = 1
            params.with_geom = 1
            params.timestamps = 1
            params.speed = 1
            this.searchTrips(params)
        },

        async searchTrips(params) {
            this.loading = true
            this.selectedTripId = null
            this.selectedTrips = []
            try {
                const { data, err } = await this.axios.get(this.tripUrl, { params }).catch((err) => ({err}))
                if (err) {
                    this.searchResults = []
                    this.showResults = true
                    this.loading = false
                    return
                }
                this.searchResults = data.map((trip) => {
                    trip.start_time = this.setTime(trip.start_time)
                    trip.end_time = this.setTime(trip.end_time)
                    return trip
                })
                this.showResults = true
            } catch (error) {
                this.searchResults = []
                this.showResults = true
            } finally {
                this.loading = false
            }
        },

        setTime(timeString) {
            if (!timeString) return timeString
            if (timeString.includes('T') && !timeString.includes('+') && !timeString.includes('Z')) {
                return timeString + 'Z'
            }
            return timeString
        },

        toggleMap() {
            this.showMap = !this.showMap
            if (this.showMap) {
                this.$nextTick(() => {
                    if (this.$refs.mapContainer) {
                        this.map = this.$refs.mapContainer.getMap()
                        if (this.map) {
                            this.map.resizeMap()
                        }
                    }
                })
            } else {
                this.selectedTripId = null
                this.map = null
            }
        },

        showTripOnMap(trip) {
            if (trip) {
                if (!this.showMap) {
                    this.toggleMap()
                }
                this.selectedTripId = trip.id
            }
        },

        mapChanged(map) {
            this.map = map
            if (this.map) {
                this.map.resizeMap()
            }
        },

        handleSelectedTrips(selectedRows) {
            this.selectedTrips = selectedRows
            this.$emit('selected-trips-changed', selectedRows.length)
        }
    }
}
</script>

<style scoped></style>
